import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Star, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePendingReviews } from "@/components/engage/usePendingReviews";

/**
 * Iter-329 optional (chased in Iter-337) — dismissible site-wide ribbon
 * while any pending review exists: "You have a discount waiting at
 * {vendor_name} — {time left} to claim it". Dismissal is per-session
 * AND per pending-set, so a NEW pending review resurfaces the ribbon.
 * Never rendered on white-label routes (App.js gates it with the
 * header).
 */
export default function PendingReviewRibbon() {
  const { user } = useAuth();
  const q = usePendingReviews(!!user);
  const rows = q.data?.pending_reviews || [];
  const key = rows.map((r) => r.session_id).sort().join(",");
  const [dismissedKey, setDismissedKey] = useState(
    () => sessionStorage.getItem("pending_ribbon_dismissed") || "",
  );
  if (!user || rows.length === 0 || dismissedKey === key) return null;

  const first = rows[0];
  const left = timeLeft(first.review_deadline);
  const href = first.qr_code_id
    ? `/engage/${first.qr_code_id}?paid=1&sid=${first.session_id}`
    : "/my/visits";

  return (
    <div className="bg-amber-500 text-white" data-testid="pending-review-ribbon">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-2 flex items-center gap-3 text-sm">
        <Star className="w-4 h-4 shrink-0" />
        <Link to={href} data-testid="pending-review-ribbon-link" className="flex-1 font-semibold hover:underline truncate">
          You have a discount waiting at {first.vendor_name || "a partner store"}
          {left ? ` — ${left} to claim it` : ""}
          {rows.length > 1 ? ` (+${rows.length - 1} more)` : ""}
        </Link>
        <button
          type="button"
          aria-label="Dismiss"
          data-testid="pending-review-ribbon-dismiss"
          onClick={() => {
            sessionStorage.setItem("pending_ribbon_dismissed", key);
            setDismissedKey(key);
          }}
          className="shrink-0 grid place-items-center w-6 h-6 rounded-full hover:bg-white/20 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function timeLeft(deadline) {
  if (!deadline) return null;
  const ms = new Date(deadline).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const h = Math.floor(ms / 3_600_000);
  if (h >= 1) return `${h} h left`;
  return `${Math.max(1, Math.floor(ms / 60_000))} min left`;
}
